import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, X, Receipt, Save, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import apiClient from '@/services/apiClient';
import { useToast } from '@/contexts/ToastContext';

interface Tenant {
    id: string;
    name: string;
    unitName: string;
    unitId: string;
    propertyId: string;
    rentAmount?: number | null;
    billableUtilities?: {
        [key: string]: {
            type: "fixed" | "variable";
            amount: number | null;
        };
    };
}

interface PaymentInstruction {
    id: string;
    type: string;
    details: any;
}

interface LineItem {
    id: string;
    description: string;
    amount: string;
}

export const CreateInvoice: React.FC = () => {
    const navigate = useNavigate();
    const { activeRole } = useAuth();
    const { warning, success, error: toastError } = useToast();
    const isStaff = activeRole === "Staff";

    const [tenants, setTenants] = useState<Tenant[]>([]);
    const [paymentInstructions, setPaymentInstructions] = useState<PaymentInstruction[]>([]);
    
    const [selectedTenantId, setSelectedTenantId] = useState<string>('');
    const [lineItems, setLineItems] = useState<LineItem[]>([]);
    const [dueDate, setDueDate] = useState<string>(new Date().toISOString().split('T')[0]);
    const [selectedInstructionId, setSelectedInstructionId] = useState<string>('');
    
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                setError(null);
                
                // Fetch tenants
                let tenantsUrl = "/invoicing/tenants";
                
                const [tenantsRes, instructionsRes, tumaRes] = await Promise.all([
                    apiClient.get<Tenant[]>(tenantsUrl),
                    apiClient.get<PaymentInstruction[]>("/payment-instructions"),
                    apiClient.get("/landlord/tuma/status").catch(() => ({ data: { is_active: false } }))
                ]);
                
                setTenants(tenantsRes.data);

                const instructions = [...instructionsRes.data];
                if (tumaRes.data?.is_active) {
                    instructions.unshift({
                        id: "tuma",
                        type: "M-Pesa (Tuma)",
                        details: { email: tumaRes.data.business_email }
                    });
                }
                setPaymentInstructions(instructions);
            } catch (err: any) {
                console.error("Failed to fetch prerequisite data", err);
                setError("Failed to fetch required data. Please try again.");
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [isStaff]);

    const selectedTenant = useMemo(() => {
        return tenants.find(t => t.id === selectedTenantId) || null;
    }, [tenants, selectedTenantId]);

    const handleLineItemChange = (id: string, field: "description" | "amount", value: string) => {
        if (id.startsWith("locked-")) {
            warning('Cannot edit', 'This charge is fixed and cannot be edited.');
            return;
        }
        setLineItems(current => 
            current.map(item => item.id === id ? { ...item, [field]: value } : item)
        );
    };

    const addCustomLineItem = () => {
        setLineItems([
            ...lineItems,
            { id: Math.random().toString(), description: "", amount: "" }
        ]);
    };

    const removeLineItem = (id: string) => {
        if (id.startsWith("locked-")) {
            warning('Cannot remove', 'This is a standard charge and cannot be removed.');
            return;
        }
        setLineItems(current => current.filter(item => item.id !== id));
    };

    const handleAddCharge = (name: string) => {
        if (lineItems.some(item => item.description.toLowerCase() === name.toLowerCase())) {
            warning('Already added', `The ${name} charge has already been added.`);
            return;
        }

        if (name.toLowerCase() === "rent") {
            if (selectedTenant?.rentAmount && selectedTenant.rentAmount > 0) {
                setLineItems(prev => [
                    { id: "locked-rent", description: "Rent", amount: selectedTenant.rentAmount!.toString() },
                    ...prev
                ]);
            } else {
                warning('No rent amount', 'This tenant does not have a rent amount specified in their lease agreement.');
            }
            return;
        }

        const utilityInfo = selectedTenant?.billableUtilities?.[name];
        if (utilityInfo) {
            if (utilityInfo.type === "fixed" && utilityInfo.amount) {
                setLineItems(prev => [
                    ...prev,
                    { id: `locked-${name.toLowerCase()}`, description: name, amount: utilityInfo.amount!.toString() }
                ]);
            } else {
                setLineItems(prev => [
                    ...prev,
                    { id: Math.random().toString(), description: name, amount: "" }
                ]);
            }
        }
    };

    const invoiceTotal = useMemo(() => {
        return lineItems.reduce((total, item) => total + (parseFloat(item.amount) || 0), 0);
    }, [lineItems]);

    const handleGenerateInvoice = async () => {
        if (!selectedTenant || lineItems.length === 0 || lineItems.some(i => !i.description || !i.amount) || !selectedInstructionId || !dueDate) {
            warning('Incomplete form', 'Please select a tenant, add at least one charge, fill all amounts, and select payment instructions/due date.');
            return;
        }

        setIsSubmitting(true);
        try {
            const invoiceData = {
                tenantId: selectedTenant.id,
                unitId: selectedTenant.unitId,
                propertyId: selectedTenant.propertyId,
                dueDate: dueDate,
                lineItems: lineItems.map(item => ({
                    description: item.description,
                    amount: parseFloat(item.amount)
                })),
                paymentInstructionId: selectedInstructionId
            };
            
            await apiClient.post("/invoicing", invoiceData);
            success('Invoice created', `Invoice for ${selectedTenant.name} has been successfully created.`);
            navigate('/landlord/financials');
        } catch (err: any) {
            console.error("Error creating invoice", err);
            toastError('Failed to create invoice', err.response?.data?.error || 'An unexpected error occurred.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex h-[50vh] items-center justify-center">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6 text-center text-red-500">
                <p>{error}</p>
            </div>
        );
    }

    const inputClass = "w-full px-4 py-3 bg-light-input dark:bg-dark-input border border-light-border dark:border-dark-border rounded-xl text-light-text dark:text-dark-text outline-none focus:ring-2 focus:ring-primary/20";
    const labelClass = "block text-sm font-semibold text-light-subtle dark:text-dark-subtle mb-2";

    return (
        <div className="max-w-3xl mx-auto pb-12 space-y-6">
            <div className="flex items-center gap-4 bg-light-card dark:bg-dark-card p-4 rounded-lg shadow-sm border border-light-border dark:border-dark-border">
                <button
                    onClick={() => navigate(-1)}
                    className="p-2 rounded-full hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                >
                    <ArrowLeft size={24} className="text-primary dark:text-golden" />
                </button>
                <h1 className="text-xl font-bold text-light-text dark:text-dark-text">
                    New Manual Invoice
                </h1>
            </div>

            <div className="bg-light-card dark:bg-dark-card p-6 rounded-xl shadow-sm border border-light-border dark:border-dark-border space-y-8">
                {/* STEP 1: Tenant */}
                <div className="space-y-4">
                    <h2 className="text-lg font-bold text-primary border-b border-light-border dark:border-dark-border pb-2">
                        STEP 1: Choose the Recipient
                    </h2>
                    <div>
                        <select 
                            value={selectedTenantId} 
                            onChange={(e) => {
                                setSelectedTenantId(e.target.value);
                                setLineItems([]);
                            }}
                            className={inputClass}
                        >
                            <option value="">-- Select a Tenant --</option>
                            {tenants.map(t => (
                                <option key={t.id} value={t.id}>
                                    {t.name} - Unit {t.unitName}
                                </option>
                            ))}
                        </select>
                        {tenants.length === 0 && (
                            <p className="text-sm text-red-500 mt-2">No active tenants found.</p>
                        )}
                    </div>
                </div>

                {/* STEP 2: Charges */}
                {selectedTenant && (
                    <div className="space-y-4">
                        <h2 className="text-lg font-bold text-primary border-b border-light-border dark:border-dark-border pb-2">
                            STEP 2: Add Charges
                        </h2>
                        
                        <div className="flex flex-wrap gap-3 mb-4 border-b border-light-border dark:border-dark-border pb-4">
                            <button
                                onClick={() => handleAddCharge("Rent")}
                                className="flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-lg font-medium hover:bg-primary/20 transition-colors"
                            >
                                <Receipt size={18} /> Add Rent
                            </button>
                            
                            {Object.keys(selectedTenant.billableUtilities || {}).map(key => (
                                <button
                                    key={key}
                                    onClick={() => handleAddCharge(key)}
                                    className="flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-lg font-medium hover:bg-primary/20 transition-colors"
                                >
                                    <Receipt size={18} /> {key}
                                </button>
                            ))}

                            <button
                                onClick={addCustomLineItem}
                                className="flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-lg font-medium hover:bg-primary/20 transition-colors"
                            >
                                <Plus size={18} /> Add Custom
                            </button>
                        </div>

                        {/* Line Items List */}
                        <div className="space-y-3">
                            {lineItems.map((item, index) => {
                                const isLocked = item.id.startsWith("locked-");
                                return (
                                    <div key={item.id} className="flex gap-3 items-center">
                                        <input
                                            type="text"
                                            value={item.description}
                                            onChange={(e) => handleLineItemChange(item.id, 'description', e.target.value)}
                                            placeholder={`Item #${index + 1} Description`}
                                            disabled={isLocked}
                                            className={`${inputClass} flex-1 ${isLocked ? 'opacity-60 cursor-not-allowed' : ''}`}
                                        />
                                        <input
                                            type="number"
                                            value={item.amount}
                                            onChange={(e) => handleLineItemChange(item.id, 'amount', e.target.value)}
                                            placeholder="Amount"
                                            disabled={isLocked}
                                            className={`${inputClass} w-32 ${isLocked ? 'opacity-60 cursor-not-allowed' : ''}`}
                                        />
                                        {!isLocked && (
                                            <button 
                                                onClick={() => removeLineItem(item.id)}
                                                className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                            >
                                                <X size={24} />
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        
                        {lineItems.length > 0 && (
                            <div className="flex justify-between items-center mt-6 pt-4 border-t border-light-border dark:border-dark-border">
                                <span className="text-lg font-semibold text-light-text dark:text-dark-text">Invoice Total:</span>
                                <span className="text-xl font-bold text-primary">KES {invoiceTotal.toLocaleString()}</span>
                            </div>
                        )}
                    </div>
                )}

                {/* STEP 3: Details */}
                <div className="space-y-4">
                    <h2 className="text-lg font-bold text-primary border-b border-light-border dark:border-dark-border pb-2">
                        STEP 3: Set Due Date & Payment Method
                    </h2>
                    
                    <div>
                        <label className={labelClass}>Invoice Due Date</label>
                        <input
                            type="date"
                            value={dueDate}
                            onChange={(e) => setDueDate(e.target.value)}
                            className={inputClass}
                        />
                    </div>

                    <div>
                        <label className={labelClass}>Payment Instructions</label>
                        <select
                            value={selectedInstructionId}
                            onChange={(e) => setSelectedInstructionId(e.target.value)}
                            className={inputClass}
                            disabled={paymentInstructions.length === 0}
                        >
                            <option value="">-- Select Payment Method --</option>
                            {paymentInstructions.map(instr => (
                                <option key={instr.id} value={instr.id}>
                                    {instr.type === "M-Pesa (Tuma)" 
                                        ? "M-Pesa (Instant) via Tuma" 
                                        : `${instr.type} - ${instr.details.paybill || instr.details.accountNumber}`}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="pt-4">
                    <button
                        onClick={handleGenerateInvoice}
                        disabled={isSubmitting || !selectedTenant}
                        className="w-full flex items-center justify-center gap-2 py-4 bg-primary text-white rounded-xl font-bold text-lg hover:opacity-90 disabled:opacity-50 transition-opacity"
                    >
                        {isSubmitting ? (
                            <><Loader2 className="animate-spin" size={20} /> Generating...</>
                        ) : (
                            <><Save size={20} /> Generate & Send Invoice</>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};
